import { router } from '@inertiajs/react'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { LitellmModel } from './types'

export type CatalogStatus = {
  source: 'live' | 'fallback' | 'unknown'
  count: number
  via?: 'gateway' | 'broker'
  reason?: 'empty_registry' | 'unconfigured' | 'unauthorized' | 'unreachable' | 'broker_unavailable'
}

type Props = {
  catalog: LitellmModel[]
  status?: CatalogStatus
  refreshUrl?: string
}

const REASON_HINTS: Record<NonNullable<CatalogStatus['reason']>, string> = {
  empty_registry: 'gateway respondeu mas o registry está vazio (modelos provavelmente em proxy_config.yaml, não via API admin)',
  unconfigured: 'LITELLM_MASTER_KEY ausente no env do workstation',
  unauthorized: 'LITELLM_MASTER_KEY inválida ou sem permissão admin',
  unreachable: 'gateway inacessível (timeout / conexão / HTTP erro)',
  broker_unavailable: 'nenhum broker Fleet API conseguiu listar o catálogo LiteLLM',
}

// Compact status pill above the LiteLLM sections. Click "Atualizar" to force
// a refresh of the cached catalog via the workstation refresh endpoint.
export function CatalogStatusBar({ catalog, status, refreshUrl }: Props) {
  const [refreshing, setRefreshing] = useState(false)
  const empty = catalog.length === 0
  const isFallback = status?.source === 'fallback'
  const reasonHint = status?.reason ? REASON_HINTS[status.reason] : null
  const liveSource = status?.via === 'broker' ? 'broker Fleet API' : 'gateway'

  const refresh = () => {
    if (!refreshUrl) return
    setRefreshing(true)
    router.post(refreshUrl, {}, {
      preserveScroll: true,
      onFinish: () => setRefreshing(false),
    })
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
      <div className="flex items-start gap-2">
        {empty ? (
          <>
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <div className="text-muted-foreground">
              <div>Catálogo LiteLLM indisponível.</div>
              {reasonHint && <div className="mt-0.5 text-[11px]">{reasonHint}</div>}
            </div>
          </>
        ) : isFallback ? (
          <>
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <div className="text-muted-foreground">
              <div>
                Usando <strong className="font-medium text-foreground">fallback hardcoded</strong> ({catalog.length} modelos).
              </div>
              {reasonHint && <div className="mt-0.5 text-[11px]">{reasonHint}</div>}
            </div>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
            <span className="text-muted-foreground">
              <strong className="font-medium text-foreground">{catalog.length}</strong> modelos disponíveis via {liveSource}
            </span>
          </>
        )}
      </div>
      {refreshUrl && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={refreshing}
              onClick={refresh}
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Re-busca a lista de modelos do LiteLLM gateway (cache de 5 min).</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
